import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

const SHOW_DELAY_MS = 200;

/**
 * Lightweight hover/focus tip for XML tag names.
 * Content matches form field defs / ISO element dictionary — no browser title.
 * Tip stays interactive so the text caret/I-beam is visible for selection/copy.
 */
export function XmlTagTooltip({
  text,
  className,
  children,
}: {
  text: string | undefined;
  className?: string;
  children: ReactNode;
}) {
  const tipId = useId();
  const [visible, setVisible] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  if (!text) {
    return <span className={className}>{children}</span>;
  }

  const show = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
  };
  const hide = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setVisible(false);
  };

  return (
    <span
      className={cn('relative inline-block', className)}
      aria-describedby={visible ? tipId : undefined}
      aria-label={text}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {visible && (
        <span
          id={tipId}
          role="tooltip"
          className={cn(
            // pb-3 bridges the visual gap so the pointer can move onto the tip
            // without firing mouseLeave (which would dismiss it).
            'absolute bottom-full left-0 z-30 cursor-text select-text pb-3',
            'max-w-[min(72rem,85vw)]',
          )}
        >
          <span
            className={cn(
              'block border border-rule bg-surface px-6 py-4',
              'font-sans text-[15.6px] leading-snug font-normal normal-case tracking-normal text-ink shadow-none',
            )}
          >
            {text}
          </span>
        </span>
      )}
    </span>
  );
}
